import React, { useEffect, useRef, useMemo, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  ZoomControl,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-routing-machine";
import "leaflet-routing-machine/dist/leaflet-routing-machine.css";

import { Button } from "./ui/button";
import { Text } from "./catalyst/text";
import { Badge } from "./ui/badge";
// Removed FeedbackComponent import as it's likely handled by the parent page

// Icon fix - use CDN icons to avoid missing files
const defaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

// Monkey patch the Leaflet Routing Machine to fix the removeLayer error
if (L.Routing && L.Routing.Line) {
  const originalClearLines = L.Routing.Line.prototype._clearLines;
  L.Routing.Line.prototype._clearLines = function() {
    try {
      // Check if this._map exists and is valid before calling removeLayer
      if (this._map && this._map.removeLayer && this._map.getContainer) {
        // Extra safety check for container
        try {
          const container = this._map.getContainer();
          if (!container || !container._leaflet_id) {
            // Map container is invalid, just clear routes array
            if (this._routes) {
              this._routes = [];
            }
            return;
          }
        } catch (e) {
          // Can't access container, just clear routes
          if (this._routes) {
            this._routes = [];
          }
          return;
        }
        
        // Safe removal of route layers
        if (this._routes && Array.isArray(this._routes)) {
          const routesToRemove = [...this._routes]; // Copy array to avoid modification issues
          this._routes = []; // Clear the array first
          
          routesToRemove.forEach((route) => {
            if (route) {
              try {
                if (this._map.hasLayer && this._map.hasLayer(route)) {
                  this._map.removeLayer(route);
                }
              } catch (e) {
                // Silently ignore individual layer removal errors
              }
            }
          });
        }
        
        // Clear alternatives if they exist
        if (this._alternatives && Array.isArray(this._alternatives)) {
          const altsToRemove = [...this._alternatives];
          this._alternatives = [];
          
          altsToRemove.forEach((alt) => {
            if (alt) {
              try {
                if (this._map.hasLayer && this._map.hasLayer(alt)) {
                  this._map.removeLayer(alt);
                }
              } catch (e) {
                // Silently ignore
              }
            }
          });
        }
      } else {
        // If map is not valid, just clear the arrays
        if (this._routes) {
          this._routes = [];
        }
        if (this._alternatives) {
          this._alternatives = [];
        }
      }
    } catch (error) {
      // Ensure routes are cleared even if there's an error
      if (this._routes) {
        this._routes = [];
      }
      if (this._alternatives) {
        this._alternatives = [];
      }
    }
  };
}

// Helper function to parse location
const parseLocation = (loc) => {
  const fallbackCenter = [39.8283, -98.5795]; // Define fallback directly
  
  // Handle coordinate object format { lat: number, lng: number }
  if (loc && typeof loc === "object" && "lat" in loc && "lng" in loc) {
    const lat = parseFloat(loc.lat);
    const lng = parseFloat(loc.lng);
    if (!isNaN(lat) && !isNaN(lng)) {
      return [lat, lng];
    }
  }
  
  // Handle coordinate string format "(lat,lng)" or "lat,lng"
  if (loc && typeof loc === "string") {
    try {
      const clean = loc.replace(/[()]/g, "");
      const parts = clean.split(",");
      if (parts.length === 2) {
        const lat = parseFloat(parts[0].trim());
        const lng = parseFloat(parts[1].trim());
        if (!isNaN(lat) && !isNaN(lng)) {
          
          return [lat, lng];
        }
      }
    } catch (error) {
      // If coordinate parsing fails, this might be an address string
      // For now, return fallback - in the future, this could trigger geocoding
    }
  }
  
  return fallbackCenter;
};

// MapBounds Component (Handles fitting markers)
const MapBounds = ({ customers }) => {
  const map = useMap();
  useEffect(() => {
    if (customers && customers.length > 0 && map) {
      try {
        // Check if map is still valid
        if (!map.getContainer() || !map.getContainer()._leaflet_id) {
          return;
        }
        
        const bounds = L.latLngBounds([]);
        let validLocationsCount = 0;
        customers.forEach((customer) => {
          let lat, lng;
          
          // Handle both formats: separate lat/lng fields or location string
          if (customer?.lat !== undefined && customer?.lng !== undefined) {
            lat = customer.lat;
            lng = customer.lng;
          } else if (customer?.location) {
            [lat, lng] = parseLocation(customer.location);
          } else {
            return;
          }
          
          // Only include if coordinates are valid and not the fallback center
          const isValidCoords = isFinite(lat) && isFinite(lng) && 
                               (lat !== 39.8283 || lng !== -98.5795);
          if (isValidCoords) {
            bounds.extend([lat, lng]);
            validLocationsCount++;
          }
        });
        if (validLocationsCount > 0) {
          map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 }); // Add maxZoom here
        }
      } catch (error) {
      }
    }
  }, [map, customers]);
  return null;
};

// RouteLines Component (Handles drawing route)
const RouteLines = ({ directionsRequest }) => {
  const map = useMap();
  const routeControlRef = useRef(null);
  const routeIdRef = useRef(null);
  const isUpdatingRef = useRef(false);

  // Safe wrapper for clearing route lines
  const safeClearRouteLines = (routeControl) => {
    if (!routeControl) return;
    
    try {
      // Alternative method: Try to clear routes through the plan first
      const plan = routeControl.getPlan && routeControl.getPlan();
      if (plan) {
        if (plan.setWaypoints && typeof plan.setWaypoints === 'function') {
          try {
            plan.setWaypoints([]);
          } catch (e) {
            // Silently ignore waypoint clearing errors
          }
        }
        if (plan._routes) {
          plan._routes = [];
        }
      }
      
      // Only attempt _clearLines if we have a valid router with map
      if (routeControl._router && typeof routeControl._router._clearLines === 'function') {
        // Check if the router has a valid map reference
        if (routeControl._router._map && routeControl._router._map.getContainer) {
          try {
            // Additional check to ensure the map container is still in the DOM
            const container = routeControl._router._map.getContainer();
            if (container && container._leaflet_id && document.body.contains(container)) {
              routeControl._router._clearLines();
            } else {
            }
          } catch (e) {
            // Silently handle any errors during _clearLines
          }
        }
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    // Cleanup function to remove control when component unmounts or request changes significantly
    return () => {
      if (routeControlRef.current && map) {
        try {
          // Check if map and its container are still valid before cleanup
          if (map.getContainer && map.getContainer() && map.getContainer()._leaflet_id && map.hasLayer) {
            // Additional safety check - only remove if the control is actually on the map
            if (map.hasLayer && typeof map.hasLayer === 'function') {
              // For routing control, we need to check if it exists in the map's controls
              try {
                // Safely clear route lines first
                const plan = routeControlRef.current.getPlan && routeControlRef.current.getPlan();
                if (plan && plan.setWaypoints && typeof plan.setWaypoints === 'function') {
                  plan.setWaypoints([]);
                }
                
                // Use safe wrapper to clear route lines
                safeClearRouteLines(routeControlRef.current);
              } catch (planError) {
              }
              
              // Safe removal of the control with enhanced error handling
              if (routeControlRef.current && typeof map.removeControl === 'function') {
                try {
                  map.removeControl(routeControlRef.current);
                } catch (removeError) {
                  // If removeControl fails, try to manually remove from map's controls
                  if (map._controlContainer && routeControlRef.current._container) {
                    try {
                      if (routeControlRef.current._container.parentNode) {
                        routeControlRef.current._container.parentNode.removeChild(routeControlRef.current._container);
                      }
                    } catch (domError) {
                    }
                  }
                }
              }
            }
          }
        } catch (e) {
          // Silently handle cleanup errors - this is expected during rapid state changes
        } finally {
          routeControlRef.current = null;
        }
      }
    };
  }, [map]); // Only depends on map for cleanup

  useEffect(() => {
    // Prevent rapid updates that can cause null reference errors
    if (isUpdatingRef.current) {
      return;
    }
    
    // *** Check if directionsRequest is valid before proceeding ***
    if (
      !directionsRequest?.origin?.location ||
      !directionsRequest?.destination?.location
    ) {
      // If request is invalid or null (like in preview), ensure any existing route is removed
      if (routeControlRef.current && map) {
        try {
          // Check if map and its container are still valid before cleanup
          if (map.getContainer && map.getContainer() && map.getContainer()._leaflet_id) {
            try {
              // Safely clear route lines first
              const plan = routeControlRef.current.getPlan && routeControlRef.current.getPlan();
              if (plan && plan.setWaypoints && typeof plan.setWaypoints === 'function') {
                plan.setWaypoints([]);
              }
              
              // Additional safety: Try to clear any route lines manually
              if (routeControlRef.current._router && routeControlRef.current._router._clearLines) {
                try {
                  routeControlRef.current._router._clearLines();
                } catch (clearError) {
                }
              }
            } catch (planError) {
            }
            
            // Remove the control from the map with better error handling
            if (typeof map.removeControl === 'function' && routeControlRef.current) {
              try {
                map.removeControl(routeControlRef.current);
              } catch (removeError) {
                // If removeControl fails, try to manually remove from map's controls
                if (map._controlContainer && routeControlRef.current._container) {
                  try {
                    if (routeControlRef.current._container.parentNode) {
                      routeControlRef.current._container.parentNode.removeChild(routeControlRef.current._container);
                    }
                  } catch (domError) {
                  }
                }
              }
            }
          }
        } catch (e) {
        } finally {
          routeControlRef.current = null;
          routeIdRef.current = null;
        }
      }
      return; // Exit early if no valid request
    }

    // Proceed with route creation only if request is valid
    try {
      isUpdatingRef.current = true;
      
      const allPoints = [
        directionsRequest.origin,
        ...(directionsRequest.waypoints || []),
        directionsRequest.destination,
      ];

      const waypoints = allPoints
        .map((point) => {
          if (!point?.location) return null;
          const coords = parseLocation(point.location);
          // Only create waypoint if we have valid coordinates (not fallback)
          const isValidCoords = coords[0] !== 39.8283 || coords[1] !== -98.5795;
          return (coords && isValidCoords) ? L.latLng(coords[0], coords[1]) : null;
        })
        .filter(Boolean);

      if (waypoints.length < 2) return;

      const routeId = JSON.stringify(waypoints.map((w) => [w.lat, w.lng]));

      if (routeId !== routeIdRef.current) {
        routeIdRef.current = routeId;

        if (routeControlRef.current && map) {
          try {
            // Check if map and its container are still valid before cleanup
            if (map.getContainer && map.getContainer() && map.getContainer()._leaflet_id) {
              try {
                // Safely clear route lines first
                const plan = routeControlRef.current.getPlan && routeControlRef.current.getPlan();
                if (plan && plan.setWaypoints && typeof plan.setWaypoints === 'function') {
                  plan.setWaypoints([]);
                }
                
                // Use safe wrapper to clear route lines
                safeClearRouteLines(routeControlRef.current);
              } catch (planError) {
              }
              
              // Remove the control from the map
              if (typeof map.removeControl === 'function' && routeControlRef.current) {
                try {
                  map.removeControl(routeControlRef.current);
                } catch (removeError) {
                  // If removeControl fails, try to manually remove from map's controls
                  if (map._controlContainer && routeControlRef.current._container) {
                    try {
                      if (routeControlRef.current._container.parentNode) {
                        routeControlRef.current._container.parentNode.removeChild(routeControlRef.current._container);
                      }
                    } catch (domError) {
                    }
                  }
                }
              }
            }
          } catch (e) {
          } finally {
            routeControlRef.current = null;
          }
        }

        // Create route control with enhanced error handling
        let routeControl;
        try {
          // Check if map is still valid before creating control
          if (!map || !map.getContainer || !map.getContainer()) {
            return;
          }
          
          routeControl = L.Routing.control({
            waypoints: waypoints,
            routeWhileDragging: false,
            show: false,
            addWaypoints: false,
            fitSelectedRoutes: false, // We use MapBounds
            lineOptions: {
              styles: [
                { color: "#3498db", opacity: 0.8, weight: 6 },
                { color: "#ffffff", opacity: 0.4, weight: 8 } // White outline for better visibility
              ],
            },
            createMarker: () => null,
            router: L.Routing.osrmv1({
              serviceUrl: "https://router.project-osrm.org/route/v1",
              timeout: 10000, // 10 second timeout
              // Enable route optimization by requesting optimized order
              profile: 'driving', // Specify driving profile for better route calculation
            }),
            // Enable waypoint optimization if supported by the request
            waypointMode: 'snap',
            addWaypoints: false, // Prevent adding waypoints by clicking
            draggableWaypoints: false, // Prevent dragging waypoints
          });
          
          // Override the onRemove method to prevent errors during cleanup
          const originalOnRemove = routeControl.onRemove;
          routeControl.onRemove = function(map) {
            try {
              // Clear any pending route calculations
              if (this._router && this._router._pendingRequest) {
                this._router._pendingRequest.abort();
              }
              
              // Call safe clear lines
              safeClearRouteLines(this);
              
              // Call original onRemove if it exists
              if (originalOnRemove && typeof originalOnRemove === 'function') {
                originalOnRemove.call(this, map);
              }
            } catch (e) {
            }
          };

          // Safely add to map with error handling
          if (map && typeof map.addControl === 'function') {
            map.addControl(routeControl);
          } else {
            throw new Error('Map or addControl method not available');
          }

          routeControlRef.current = routeControl;
        } catch (routeCreationError) {
          // Clean up if route control was created but not added
          if (routeControl && routeControl.remove && typeof routeControl.remove === 'function') {
            try {
              routeControl.remove();
            } catch (removeError) {
            }
          }
          return; // Exit early if route control creation failed
        }

        // Enhanced event handling for directions callback with better logging
        routeControl.on("routesfound", (e) => {
          if (e.routes.length > 0) {
            const route = e.routes[0];
            
            if (directionsRequest.handleDirectionsCallback) {
              directionsRequest.handleDirectionsCallback({ 
                routes: e.routes,
                optimized: directionsRequest.optimizeWaypoints,
                routeType: directionsRequest.routeType || "standard"
              });
            }
          }
        });
        
        routeControl.on("routingerror", (e) => {
          if (directionsRequest.handleDirectionsCallback) {
            directionsRequest.handleDirectionsCallback({ 
              routes: [], 
              error: e.error?.message || "Routing failed",
              routeType: directionsRequest.routeType || "standard"
            });
          }
        });

        // Add route start event for loading feedback
        routeControl.on("routingstart", () => {
        });
      }
    } catch (error) {
    } finally {
      // Always reset the updating flag
      isUpdatingRef.current = false;
    }

    // IMPORTANT: Dependency array *must* include directionsRequest to re-run when it changes
  }, [directionsRequest, map]);

  return null;
};

// NumberedMarker Component
const NumberedMarker = ({ position, index, customer, isCurrentPage = false, isHovered = false }) => {
  
  // Different styling for current page vs all customers vs hovered
  let markerClasses;
  if (isHovered) {
    markerClasses = 'bg-orange-500 border-3 border-orange-700 text-white shadow-xl scale-125 animate-pulse'; // Hovered: orange with pulse
  } else if (isCurrentPage) {
    markerClasses = 'bg-blue-600 border-2 border-blue-800 text-white shadow-lg scale-110'; // Paginated customers: prominent blue
  } else {
    markerClasses = 'bg-blue-400 text-white shadow-sm'; // All other customers: lighter blue
  }
    
  const divIcon = L.divIcon({
    className: "custom-marker-icon",
    html: `<div class="flex items-center justify-center w-6 h-6 rounded-full ${markerClasses} text-xs font-bold">${index + 1}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  });

  const scrollToCustomer = () => {
    const customerElement = document.getElementById(`customer-${index}`);
    if (customerElement) {
      customerElement.scrollIntoView({ behavior: "smooth", block: "nearest" }); // 'nearest' might be less jarring
      customerElement.style.transition = "background-color 0.3s ease";
      customerElement.style.backgroundColor = "hsl(var(--accent))";
      setTimeout(() => {
        customerElement.style.backgroundColor = "";
      }, 1500);
    }
  };

  return (
    <Marker position={position} icon={divIcon}>
    </Marker>
  );
};

// Main MapSection component
const MapSection = ({
  customers,
  directionsRequest,
  handleDirectionsCallback,
  currentPageCustomerIds = [], // Array of customer IDs on current page
  hoveredCustomerId = null, // Customer ID being hovered in calendar
}) => {
  const defaultCenter = [39.8283, -98.5795];
  const defaultZoom = 4;
  const [mapKey, setMapKey] = useState(0);

  const callbackRef = useRef(handleDirectionsCallback);
  useEffect(() => {
    callbackRef.current = handleDirectionsCallback;
  }, [handleDirectionsCallback]);

  // Error boundary to reset map on critical errors
  useEffect(() => {
    const handleError = (event) => {
      if (event.error && event.error.message && event.error.message.includes('_leaflet_pos')) {
        setMapKey(prev => prev + 1);
      }
    };
    
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  const directionsCallbackWrapper = (response) => {
    if (callbackRef.current) callbackRef.current(response);
  };

  // Pass the wrapper to the request object IF the original request exists
  const formattedDirectionsRequest = directionsRequest
    ? {
        ...directionsRequest,
        handleDirectionsCallback: directionsCallbackWrapper,
      }
    : null; // Pass null if no directions are requested (for preview map)

  return (
    <div className="h-full w-full relative bg-muted/10">
      {" "}
      {/* Added background */}
      <MapContainer
        key={mapKey}
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ width: "100%", height: "100%" }}
        scrollWheelZoom={true}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ZoomControl position="bottomright" />
        <MapBounds customers={customers} />

        {useMemo(() => 
          customers?.map((customer, idx) => {
            let lat, lng;
            
            // Handle both formats: separate lat/lng fields or location string
            if (customer?.lat !== undefined && customer?.lng !== undefined) {
              lat = customer.lat;
              lng = customer.lng;
            } else if (customer?.location) {
              [lat, lng] = parseLocation(customer.location);
            } else {
              return null;
            }
            
            // Only render marker if coordinates are valid and not the fallback center
            const isValidCoords = isFinite(lat) && isFinite(lng) && 
                                 (lat !== 39.8283 || lng !== -98.5795);
            if (!isValidCoords) return null;
            
            
            // Check if this customer is on the current page
            const customerId = customer.customer_id || customer.id;
            const isCurrentPage = currentPageCustomerIds.includes(customerId);
            const isHovered = hoveredCustomerId && hoveredCustomerId === customerId;
            
            return (
              <NumberedMarker
                key={`marker-${idx}-${customer._uniqueId || customer.id || customer.customername}`}
                position={[lat, lng]}
                index={idx}
                customer={customer}
                isCurrentPage={isCurrentPage}
                isHovered={isHovered}
              />
            );
          }).filter(Boolean), [customers, currentPageCustomerIds, hoveredCustomerId]
        )}

        {/* Only render RouteLines if there's a valid request */}
        {formattedDirectionsRequest && (
          <RouteLines directionsRequest={formattedDirectionsRequest} />
        )}
      </MapContainer>
    </div>
  );
};

export default MapSection;
